import React, { useRef, useState } from 'react';

import mime from 'mime-types';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import smartlookClient from 'smartlook-client';

import Tooltip from '@material-ui/core/Tooltip';

import { addNotificationAction } from '../../../action/notifications/notifications';

import { UiButton } from '../../../ui/ui-button';

import { isProd } from '../../../config/config';

type InputProps = {
  onVariableSelect: (variableValue: string) => void;
  className?: string;
  languageCode?: string;
  disabled?: boolean;
  urlFileMappings?: Array<UrlFileMapping>;
  onUploadFile?: (file: File) => Promise<string>;
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    addNotification: (settings: { key: string; message: string }) =>
      dispatch(addNotificationAction(settings)),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

// Only restrict executables
const RESTRICTED_CONTENT_TYPES = ['application/x-msdownload'];
const FILE_SIZE_LIMIT_IN_GB = 1;
const MAX_FILES_PER_MESSAGE = 3; // To make for the best end-user experience, we're limiting the maximum uploads to 3

export const UploadFileButton = connect(
  null,
  mapDispatchToProps,
)(
  ({
    onVariableSelect,
    className,
    languageCode,
    onUploadFile,
    actions,
    disabled,
    urlFileMappings,
  }: Props) => {
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const isExceedingUploadLimit =
      urlFileMappings &&
      urlFileMappings.filter((u) => !u.isDeleted).length >= MAX_FILES_PER_MESSAGE;

    const handleUploadClick = async ({
      target: { files },
    }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files?.length || !onUploadFile) {
        return;
      }

      const file = files[0];
      const contentType = mime.lookup(file.name);
      if (!contentType || RESTRICTED_CONTENT_TYPES.includes(contentType)) {
        actions.addNotification({
          key: 'file-upload-button-file-extension',
          message: 'Invalid file type.',
        });
        return;
      }

      const fileSizeInGb = file.size / 1024 / 1024 / 1024;
      if (fileSizeInGb > FILE_SIZE_LIMIT_IN_GB) {
        actions.addNotification({
          key: 'file-upload-button-file-size',
          message: 'File is too large.',
        });
        return;
      }

      if (isProd && smartlookClient) {
        const smartlookUploadFileEvent = {
          eventName: 'upload file in messages click',
          props: {
            fileName: file.name,
            fileSizeInMb: (fileSizeInGb * 1024).toPrecision(2),
          },
        };
        smartlookClient.track(smartlookUploadFileEvent.eventName, smartlookUploadFileEvent.props);
      }

      setIsUploading(true);
      try {
        const link = await onUploadFile(file);
        onVariableSelect(link);
      } finally {
        setIsUploading(false);
      }
    };

    const appendLanguageIdentifier = (id: string) => (languageCode ? `${id}-${languageCode}` : id);

    return onUploadFile ? (
      <div className={className}>
        <Tooltip
          title={
            isExceedingUploadLimit
              ? 'You have reached the limit of file uploads for this message. Please remove a file or add more images in another message.'
              : ''
          }
          placement="right"
        >
          <label htmlFor={appendLanguageIdentifier('contained-file-upload-button')}>
            <input
              className="u-display-none"
              ref={inputRef}
              hidden
              id={appendLanguageIdentifier('contained-file-upload-button')}
              type="file"
              onChange={handleUploadClick}
              disabled={disabled || isUploading || isExceedingUploadLimit}
              data-cy={appendLanguageIdentifier('upload-file')}
            />
            <UiButton
              variant="tertiary"
              size="xxs"
              component="span"
              disabled={disabled || isUploading || isExceedingUploadLimit}
              loading={isUploading}
            >
              Upload File
            </UiButton>
          </label>
        </Tooltip>
      </div>
    ) : null;
  },
);
